import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Receipt, ArrowLeft, Ticket } from '@phosphor-icons/react';

const EUR = (v) => (Number(v) || 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });

export default function ClienteValesPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const r = await api.get(`/cliente/${id}/vales`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || 'Erro ao carregar vales.');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  if (loading) return <div className="p-8 text-[#6B7280]" data-testid="vales-loading">A carregar…</div>;
  if (error) return <div className="p-8" data-testid="vales-error"><p className="text-red-600">{error}</p><Link to="/rede/saldos" className="text-sm underline mt-2 inline-block">← Voltar</Link></div>;

  const c = data.cliente;
  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="cliente-vales-page">
      <Link to="/rede/saldos" className="inline-flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#0F1829] mb-3"><ArrowLeft size={16} /> Saldos da Rede</Link>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[#0F1829] text-white grid place-items-center"><Receipt size={20} /></div>
        <div>
          <h1 className="text-2xl font-black tracking-tighter text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>Vales — {c.NomeCompleto}</h1>
          <p className="text-sm text-[#6B7280]">{c.Email || '—'} · {c.Telemovel || 'sem telefone'}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card label="Comprados" value={EUR(data.comprados)} />
        <Card label="Usados" value={EUR(data.usados)} />
        <Card label="Saldo remanescente" value={EUR(data.saldo)} highlight />
      </div>

      <div className="border border-[#E5E7EB] rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="vales-table">
          <thead className="bg-[#FAF8F2] text-left text-[11px] uppercase tracking-wider text-[#978D78]">
            <tr>
              <th className="px-3 py-2.5">Código (Guid)</th>
              <th className="px-3 py-2.5 text-right">Valor</th>
              <th className="px-3 py-2.5 text-right">Remanescente</th>
              <th className="px-3 py-2.5 text-center">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F4F6]">
            {(data.vales || []).length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-[#9CA3AF]">Sem vales.</td></tr>
            ) : data.vales.map((v) => {
              const usado = String(v.Utilizado).toUpperCase() === 'S' || Number(v.ValorRemanescente) <= 0;
              return (
                <tr key={v.ID_Vale} data-testid={`vale-row-${v.ID_Vale}`} className="hover:bg-[#FAFAFA]">
                  <td className="px-3 py-2 font-mono text-[#374151] flex items-center gap-2"><Ticket size={14} className="text-[#978D78]" />{v.Guid}</td>
                  <td className="px-3 py-2 text-right font-mono">{EUR(v.Valor)}</td>
                  <td className="px-3 py-2 text-right font-mono">{EUR(v.ValorRemanescente)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${usado ? 'bg-gray-100 text-gray-600' : 'bg-emerald-100 text-emerald-700'}`}>
                      {usado ? 'Usado' : 'Disponível'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ label, value, highlight }) {
  return (
    <div className={`border rounded-lg px-4 py-3 ${highlight ? 'border-emerald-200 bg-emerald-50' : 'border-[#E5E7EB] bg-white'}`}>
      <p className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-medium">{label}</p>
      <p className={`text-xl font-bold ${highlight ? 'text-emerald-700' : 'text-[#0F1829]'}`}>{value}</p>
    </div>
  );
}
